/* ==========================================================================
   Lavish Custom Cabinetry - motion.js
   Every GSAP timeline and scroll behaviour on the site, one entry point.

   Metaphor: soft-close. Things arrive, decelerate, and settle.
   Rules: transform and opacity only. Never a raw scroll listener for state.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     Global guards
     --------------------------------------------------------------------- */
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var prefersReduced = mqReduce.matches;
  var isMobile = window.matchMedia('(max-width: 768px)').matches;
  var isCoarse = window.matchMedia('(pointer: coarse)').matches;
  var isLowPower = (navigator.hardwareConcurrency || 8) <= 4;
  var calm = isMobile || isLowPower || (window.LAVISH && window.LAVISH.calmMotion);

  if (calm) document.documentElement.classList.add('motion-calm');

  var hasGsap = typeof window.gsap !== 'undefined';
  var hasST = hasGsap && typeof window.ScrollTrigger !== 'undefined';
  if (hasST) gsap.registerPlugin(ScrollTrigger);

  var sections = {};
  var cleanups = [];

  function initSection(name, fn) { sections[name] = fn; }

  function runSections(root) {
    Object.keys(sections).forEach(function (name) {
      try { sections[name](root || document); }
      catch (e) { console.warn('[lavish] section "' + name + '" failed', e); }
    });
  }

  function onCleanup(fn) { cleanups.push(fn); }

  /* ---------------------------------------------------------------------
     Smooth scroll (Lenis) wired into ScrollTrigger
     --------------------------------------------------------------------- */
  var lenis = null;
  var lenisTick = null;

  function initSmoothScroll() {
    if (prefersReduced || typeof window.Lenis === 'undefined') return;
    lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1, smoothWheel: true });
    if (hasST) {
      lenis.on('scroll', ScrollTrigger.update);
      lenisTick = function (time) { if (lenis) lenis.raf(time * 1000); };
      gsap.ticker.add(lenisTick);
      gsap.ticker.lagSmoothing(0);
    } else {
      var raf = function (t) { if (!lenis) return; lenis.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
    window.lavishLenis = lenis;
  }

  // Tearing down smooth scroll has to unhook the ticker as well, or the
  // driver keeps running against a destroyed instance every frame.
  function stopSmoothScroll() {
    if (!lenis) return;
    if (lenisTick && hasGsap) { gsap.ticker.remove(lenisTick); lenisTick = null; }
    lenis.destroy();
    lenis = null;
    window.lavishLenis = null;
  }
  window.lavishStopScroll = stopSmoothScroll;

  /* ---------------------------------------------------------------------
     Reveal system
     IntersectionObserver + a CSS class. Cheap, and it degrades to "visible"
     the instant anything goes wrong.
     --------------------------------------------------------------------- */
  var revealObserver = null;

  function revealAll(root) {
    (root || document).querySelectorAll('[data-reveal]').forEach(function (el) {
      el.classList.add('is-revealed');
    });
    (root || document).querySelectorAll('.word-split').forEach(function (el) {
      el.classList.add('is-revealed');
    });
  }

  initSection('reveal', function (root) {
    if (prefersReduced) { revealAll(root); return; }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
      onCleanup(function () { revealObserver.disconnect(); revealObserver = null; });
    }

    // Auto-stagger siblings that share a parent, so markup stays clean.
    root.querySelectorAll('[data-reveal-stagger]').forEach(function (group) {
      var step = parseInt(group.getAttribute('data-reveal-stagger'), 10) || 90;
      var kids = group.querySelectorAll(':scope > [data-reveal]');
      Array.prototype.forEach.call(kids, function (kid, i) {
        if (kid.style.transitionDelay) return;
        kid.style.transitionDelay = Math.min(i * step, 640) + 'ms';
      });
    });

    root.querySelectorAll('[data-reveal]').forEach(function (el) {
      if (el.classList.contains('is-revealed')) return;
      var d = el.getAttribute('data-reveal-delay');
      if (d && !el.style.transitionDelay) el.style.transitionDelay = d + 'ms';
      revealObserver.observe(el);
    });
  });

  /* ---------------------------------------------------------------------
     Reveal failsafe

     The reveal system hides content until the observer says otherwise, so a
     silent observer would mean a blank page. Anything already past the
     trigger point but still unrevealed gets revealed directly. Runs shortly
     after boot and again whenever the tab becomes visible, because a
     backgrounded tab delivers no observer callbacks at all.
     --------------------------------------------------------------------- */
  function sweepReveals() {
    var h = window.innerHeight;
    document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < h * 0.92 && r.bottom > 0) {
        el.classList.add('is-revealed');
        if (revealObserver) revealObserver.unobserve(el);
      }
    });
    document.querySelectorAll('.word-split:not(.is-revealed)').forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < h * 0.92 && r.bottom > 0) el.classList.add('is-revealed');
    });
    document.querySelectorAll('[data-count-to]').forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (el.__lavishStart && r.top < h * 0.85 && r.bottom > 0) el.__lavishStart();
    });
  }

  /* ---------------------------------------------------------------------
     Word-by-word heading reveal
     Only splits headings whose content is plain text.
     --------------------------------------------------------------------- */
  initSection('wordSplit', function (root) {
    root.querySelectorAll('[data-split]').forEach(function (el) {
      if (el.dataset.splitDone) return;
      if (el.children.length) return;
      var words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      words.forEach(function (word, i) {
        var outer = document.createElement('span');
        outer.className = 'word';
        outer.style.setProperty('--word-i', i);
        var inner = document.createElement('span');
        inner.textContent = word;
        outer.appendChild(inner);
        el.appendChild(outer);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });
      el.classList.add('word-split');
      el.dataset.splitDone = '1';

      if (prefersReduced) { el.classList.add('is-revealed'); return; }
      if (el.hasAttribute('data-split-immediate')) {
        void el.offsetWidth;
        el.classList.add('is-revealed');
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        el.classList.add('is-revealed');
        io.disconnect();
      }, { threshold: 0.3 });
      io.observe(el);
      onCleanup(function () { io.disconnect(); });
    });
  });

  /* ---------------------------------------------------------------------
     Icon stroke lengths
     CSS animates stroke-dashoffset; the real path length has to come from
     the DOM or the draw never lands exactly on zero.
     --------------------------------------------------------------------- */
  initSection('iconDash', function (root) {
    root.querySelectorAll('.icon-anim [data-draw]').forEach(function (path) {
      if (path.dataset.dashSet) return;
      var len = 0;
      try { len = Math.ceil(path.getTotalLength()); } catch (e) { len = 200; }
      path.style.setProperty('--dash', len);
      path.dataset.dashSet = '1';
    });
  });

  /* ---------------------------------------------------------------------
     Header: shrink and solidify on scroll down, reveal on scroll up
     --------------------------------------------------------------------- */
  initSection('header', function (root) {
    var header = root.querySelector('[data-header]');
    if (!header || header.dataset.motionDone) return;
    header.dataset.motionDone = '1';

    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:80px;left:0;width:1px;height:1px;pointer-events:none';
    document.body.prepend(sentinel);

    var io = new IntersectionObserver(function (entries) {
      header.classList.toggle('is-stuck', !entries[0].isIntersecting);
    }, { threshold: 0 });
    io.observe(sentinel);
    onCleanup(function () { io.disconnect(); sentinel.remove(); });

    if (prefersReduced || !hasST) return;

    // Hide on scroll down, bring it back on scroll up.
    ScrollTrigger.create({
      start: 'top -120',
      end: 99999,
      onUpdate: function (self) {
        var down = self.direction === 1;
        header.classList.toggle('is-tucked', down && !header.classList.contains('is-open'));
      }
    });
  });

  /* ---------------------------------------------------------------------
     Hero: parallax on the image, entrance on the copy
     --------------------------------------------------------------------- */
  initSection('hero', function (root) {
    var hero = root.querySelector('[data-hero]');
    if (!hero || hero.dataset.motionDone) return;
    hero.dataset.motionDone = '1';

    var media   = hero.querySelector('[data-hero-media]');
    var content = hero.querySelector('[data-hero-content]');
    var rule    = hero.querySelector('[data-hero-rule]');
    var sub     = hero.querySelector('[data-hero-sub]');
    var cta     = hero.querySelector('[data-hero-cta]');
    var cue     = hero.querySelector('.hero__cue');
    var corner  = hero.querySelector('.hero__corner');
    var layers  = hero.querySelectorAll('[data-hero-depth]');

    if (prefersReduced) {
      if (rule) rule.style.transform = 'scaleX(1)';
      return;
    }

    /* -- Entrance: one choreographed arrival, not a pile of delays -------- */
    if (hasGsap) {
      var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.fromTo(media,
        { scale: 1.18, yPercent: 3 },
        { scale: 1, yPercent: 0, duration: 2.6, ease: 'power2.out' }, 0);

      // The glows own their own continuous CSS pulse, so GSAP stays off their
      // transform and opacity. One owner per property, or the two fight.

      if (rule) {
        tl.fromTo(rule, { scaleX: 0 }, { scaleX: 1, duration: 1.3, ease: 'power2.inOut' }, 0.85);
      }
      if (sub) {
        tl.fromTo(sub, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 1.1 }, 0.95);
      }
      if (cta) {
        tl.fromTo(cta.children, { opacity: 0, y: 22 },
          { opacity: 1, y: 0, duration: 1, stagger: 0.09 }, 1.1);
      }
      if (corner) {
        tl.fromTo(corner, { opacity: 0, x: 18 }, { opacity: 1, x: 0, duration: 1 }, 1.0);
      }
      if (cue) {
        tl.fromTo(cue, { opacity: 0 }, { opacity: 1, duration: 0.9 }, 1.4);
      }
    } else if (rule) {
      rule.style.transform = 'scaleX(1)';
    }

    /* -- Pointer parallax ------------------------------------------------
       Each layer drifts by its own depth. Written to the `translate`
       property so it composes with whatever GSAP is doing to `transform`. */
    var spot = hero.querySelector('[data-hero-spotlight]');
    if (spot && !isCoarse && !calm) {
      // Reuse the hero photograph rather than fetching a second copy.
      var heroImg = hero.querySelector('.hero__img');
      if (heroImg) {
        var applySpot = function () {
          spot.style.backgroundImage = 'url("' + (heroImg.currentSrc || heroImg.src) + '")';
        };
        heroImg.complete ? applySpot() : heroImg.addEventListener('load', applySpot, { once: true });
      }
    } else if (spot) {
      spot.remove();
      spot = null;
    }

    if (!isCoarse && !calm && layers.length) {
      var tx = 0, ty = 0, cx = 0, cy = 0, raf = null;

      var tick = function () {
        cx += (tx - cx) * 0.055;
        cy += (ty - cy) * 0.055;
        for (var i = 0; i < layers.length; i++) {
          var d = parseFloat(layers[i].getAttribute('data-hero-depth')) || 1;
          // Whole pixels only. A fractional offset on a promoted layer makes
          // the text inside it resample, which reads as permanently blurred.
          // At +-26px of drift the rounding is invisible.
          layers[i].style.translate =
            Math.round(cx * d) + 'px ' + Math.round(cy * d) + 'px';
        }
        if (Math.abs(tx - cx) > 0.05 || Math.abs(ty - cy) > 0.05) {
          raf = requestAnimationFrame(tick);
        } else { raf = null; }
      };

      var onMove = function (e) {
        var r = hero.getBoundingClientRect();
        var nx = (e.clientX - r.left) / r.width;
        var ny = (e.clientY - r.top) / r.height;
        tx = (nx - 0.5) * -26;
        ty = (ny - 0.5) * -18;
        if (spot) {
          spot.style.setProperty('--sx', (nx * 100).toFixed(2) + '%');
          spot.style.setProperty('--sy', (ny * 100).toFixed(2) + '%');
          spot.classList.add('is-on');
        }
        if (raf === null) raf = requestAnimationFrame(tick);
      };
      var onLeave = function () {
        tx = 0; ty = 0;
        if (spot) spot.classList.remove('is-on');
        if (raf === null) raf = requestAnimationFrame(tick);
      };

      hero.addEventListener('pointermove', onMove, { passive: true });
      hero.addEventListener('pointerleave', onLeave, { passive: true });
      onCleanup(function () {
        hero.removeEventListener('pointermove', onMove);
        hero.removeEventListener('pointerleave', onLeave);
        if (raf) cancelAnimationFrame(raf);
      });
    }

    /* -- Scroll exit: the scene recedes as the page moves on -------------- */
    if (!hasST || calm) return;

    gsap.to(media, {
      yPercent: 16, scale: 1.08, ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true, invalidateOnRefresh: true }
    });

    if (content) {
      gsap.to(content, {
        yPercent: -22, opacity: 0, ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: '75% top', scrub: 0.4, invalidateOnRefresh: true }
      });
    }
  });

  /* ---------------------------------------------------------------------
     Blueprint stroke lengths
     The draw keyframe animates stroke-dashoffset, so each path needs its own
     real length or the drawing never lands on zero.
     --------------------------------------------------------------------- */
  initSection('blueprint', function (root) {
    root.querySelectorAll('.blueprint [data-bp]').forEach(function (path) {
      if (path.dataset.bpSet) return;
      var len = 600;
      try { len = Math.ceil(path.getTotalLength()) + 4; } catch (e) {}
      path.style.setProperty('--bp-dash', len + 'px');
      path.dataset.bpSet = '1';
    });
  });

  /* ---------------------------------------------------------------------
     Rotating headline word
     --------------------------------------------------------------------- */
  initSection('rotator', function (root) {
    root.querySelectorAll('[data-hero-rotator]').forEach(function (box) {
      if (box.dataset.motionDone) return;
      box.dataset.motionDone = '1';

      var items = box.querySelectorAll('.hero__rot');
      if (items.length < 2 || prefersReduced) return;
      var cur = 0;

      // Measure every word once and hold the box at the widest. A box that
      // resized per word changed where the headline wrapped, so the lines
      // below it jumped on every change.
      var widths = [];
      var widest = 0;
      var measure = function () {
        var h = 0;
        widest = 0;
        for (var i = 0; i < items.length; i++) {
          var was = items[i].style.position;
          items[i].style.position = 'absolute';
          widths[i] = items[i].offsetWidth;
          widest = Math.max(widest, widths[i]);
          h = Math.max(h, items[i].offsetHeight);
          items[i].style.position = was;
        }
        box.style.height = h + 'px';
        box.style.width = widest + 'px';
      };

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(measure);
      }
      measure();

      var onResize = function () { box.style.width = ''; box.style.height = ''; measure(); };
      window.addEventListener('resize', onResize);
      onCleanup(function () { window.removeEventListener('resize', onResize); });

      var timer = setInterval(function () {
        if (document.visibilityState !== 'visible') return;
        var next = (cur + 1) % items.length;
        var leaving = items[cur];
        var nxt = items[next];

        leaving.classList.add('is-out');
        leaving.classList.remove('is-active');

        nxt.classList.add('is-in');
        // Flush so the entering word transitions from below rather than
        // snapping straight to its resting position.
        void nxt.offsetWidth;
        nxt.classList.add('is-active');

        (function (el) {
          setTimeout(function () { el.classList.remove('is-out', 'is-in'); }, 800);
        })(leaving);

        cur = next;
      }, 2600);
      onCleanup(function () { clearInterval(timer); });
    });
  });

  /* ---------------------------------------------------------------------
     Capability chips - one at a time, on the same cadence as the rotator
     --------------------------------------------------------------------- */
  initSection('heroChips', function (root) {
    root.querySelectorAll('[data-hero-chips]').forEach(function (list) {
      if (list.dataset.motionDone) return;
      list.dataset.motionDone = '1';

      var chips = list.querySelectorAll('.hero__chip');
      if (chips.length < 2 || prefersReduced) return;

      var i = 0;
      var timer = setInterval(function () {
        if (document.visibilityState !== 'visible') return;
        var cur = chips[i];
        var nxt = chips[(i + 1) % chips.length];

        cur.classList.remove('is-active');
        cur.classList.add('is-out');
        setTimeout(function () { cur.classList.remove('is-out'); }, 750);

        setTimeout(function () { nxt.classList.add('is-active'); }, 260);
        i = (i + 1) % chips.length;
      }, 3900);
      onCleanup(function () { clearInterval(timer); });
    });
  });

  /* ---------------------------------------------------------------------
     Generic parallax drift
     Off on mobile, where parallax costs more in jank than it returns.
     --------------------------------------------------------------------- */
  initSection('parallax', function (root) {
    if (prefersReduced || !hasST || calm) return;

    root.querySelectorAll('[data-parallax]').forEach(function (el) {
      if (el.dataset.motionDone) return;
      el.dataset.motionDone = '1';

      var amount = parseFloat(el.getAttribute('data-parallax')) || 0.12;
      gsap.fromTo(el,
        { yPercent: -amount * 100 },
        {
          yPercent: amount * 100,
          ease: 'none',
          scrollTrigger: {
            trigger: el.parentElement || el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
            invalidateOnRefresh: true
          }
        }
      );
    });
  });

  /* ---------------------------------------------------------------------
     Animated counters
     --------------------------------------------------------------------- */
  function easeOutCubic(p) { return 1 - Math.pow(1 - p, 3); }

  initSection('counters', function (root) {
    root.querySelectorAll('[data-count-to]').forEach(function (el) {
      if (el.dataset.countDone) return;
      el.dataset.countDone = '1';

      var target = parseFloat(el.getAttribute('data-count-to')) || 0;
      var prefix = el.getAttribute('data-count-prefix') || '';
      var suffix = el.getAttribute('data-count-suffix') || '';

      var render = function (value) {
        el.textContent = prefix + Math.round(value) + suffix;
      };

      if (prefersReduced) { render(target); return; }
      render(0);

      var started = false;
      var start = function () {
        if (started) return;
        started = true;
        io.disconnect();
        var t0 = null;
        var step = function (ts) {
          if (t0 === null) t0 = ts;
          var p = Math.min((ts - t0) / 1400, 1);
          render(target * easeOutCubic(p));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      };
      // The failsafe sweep calls this if the observer never does.
      el.__lavishStart = start;

      var io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) start();
      }, { threshold: 0.6 });
      io.observe(el);
      onCleanup(function () { io.disconnect(); });
    });
  });

  /* ---------------------------------------------------------------------
     Process timeline: the connector draws top to bottom with scroll
     --------------------------------------------------------------------- */
  initSection('timeline', function (root) {
    var tl = root.querySelector('[data-timeline]');
    if (!tl || tl.dataset.motionDone) return;
    tl.dataset.motionDone = '1';

    var line = tl.querySelector('[data-timeline-line]');
    if (!line) return;

    if (prefersReduced || !hasST) { line.style.transform = 'scaleY(1)'; return; }

    gsap.fromTo(line,
      { scaleY: 0 },
      {
        scaleY: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: tl,
          start: 'top 72%',
          end: 'bottom 78%',
          scrub: 0.6,
          invalidateOnRefresh: true
        }
      }
    );
  });

  /* ---------------------------------------------------------------------
     Marquee: eases up to speed as it enters
     --------------------------------------------------------------------- */
  initSection('marquee', function (root) {
    root.querySelectorAll('.marquee').forEach(function (m) {
      if (m.dataset.motionDone) return;
      m.dataset.motionDone = '1';
      var track = m.querySelector('.marquee__track');
      if (!track || prefersReduced) return;

      track.style.animationPlayState = 'paused';
      var io = new IntersectionObserver(function (entries) {
        track.style.animationPlayState = entries[0].isIntersecting ? 'running' : 'paused';
      }, { threshold: 0 });
      io.observe(m);
      onCleanup(function () { io.disconnect(); });
    });
  });

  /* ---------------------------------------------------------------------
     Magnetic CTA buttons - desktop only
     --------------------------------------------------------------------- */
  initSection('magnetic', function (root) {
    if (prefersReduced || isCoarse || calm) return;

    root.querySelectorAll('[data-magnetic]').forEach(function (btn) {
      if (btn.dataset.motionDone) return;
      btn.dataset.motionDone = '1';

      var raf = null;
      var tx = 0, ty = 0, cx = 0, cy = 0;

      var tick = function () {
        cx += (tx - cx) * 0.18;
        cy += (ty - cy) * 0.18;
        btn.style.translate = cx.toFixed(2) + 'px ' + cy.toFixed(2) + 'px';
        if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) {
          raf = requestAnimationFrame(tick);
        } else {
          raf = null;
          if (tx === 0 && ty === 0) btn.style.translate = '';
        }
      };
      var kick = function () { if (raf === null) raf = requestAnimationFrame(tick); };

      var onMove = function (e) {
        var r = btn.getBoundingClientRect();
        var dx = e.clientX - (r.left + r.width / 2);
        var dy = e.clientY - (r.top + r.height / 2);
        if (Math.hypot(dx, dy) > r.width / 2 + 40) { tx = 0; ty = 0; }
        else { tx = dx * 0.28; ty = dy * 0.34; }
        kick();
      };
      var onLeave = function () { tx = 0; ty = 0; kick(); };

      btn.addEventListener('pointermove', onMove);
      btn.addEventListener('pointerleave', onLeave);
      onCleanup(function () {
        btn.removeEventListener('pointermove', onMove);
        btn.removeEventListener('pointerleave', onLeave);
        if (raf) cancelAnimationFrame(raf);
      });
    });
  });

  /* ---------------------------------------------------------------------
     Cursor-follow glow - lags the pointer, which is what makes it read
     as alive rather than attached
     --------------------------------------------------------------------- */
  function initCursorGlow() {
    var glow = document.querySelector('.cursor-glow');
    if (!glow) return;
    if (prefersReduced || isCoarse || calm || window.innerWidth < 990) {
      glow.remove();
      return;
    }

    var tx = window.innerWidth / 2, ty = window.innerHeight / 2;
    var cx = tx, cy = ty, raf = null, on = false;

    var tick = function () {
      cx += (tx - cx) * 0.12;
      cy += (ty - cy) * 0.12;
      glow.style.transform = 'translate3d(' + cx.toFixed(1) + 'px,' + cy.toFixed(1) + 'px,0)';
      raf = requestAnimationFrame(tick);
    };

    var onMove = function (e) {
      tx = e.clientX; ty = e.clientY;
      if (!on) { on = true; glow.classList.add('is-on'); }
      if (raf === null) raf = requestAnimationFrame(tick);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    onCleanup(function () {
      window.removeEventListener('pointermove', onMove);
      if (raf) cancelAnimationFrame(raf);
    });
  }

  /* ---------------------------------------------------------------------
     The monogram draw-on
     Once per session. Skipped entirely under reduced motion.
     --------------------------------------------------------------------- */
  function initIntro() {
    var veil = document.getElementById('intro-veil');
    if (!veil) return;

    var skip = prefersReduced ||
               (window.LAVISH && window.LAVISH.editor) ||
               sessionStorage.getItem('lavish-intro') === 'seen';

    if (skip) { veil.remove(); return; }

    var path = veil.querySelector('.logo-mark__path');
    if (!path) { veil.remove(); return; }

    var len;
    try { len = Math.ceil(path.getTotalLength()); } catch (e) { veil.remove(); return; }

    veil.hidden = false;
    document.documentElement.classList.add('intro-running');
    if (lenis) lenis.stop();

    path.style.strokeDasharray = len;
    path.style.strokeDashoffset = len;

    var done = function () {
      document.documentElement.classList.remove('intro-running');
      if (lenis) lenis.start();
      sessionStorage.setItem('lavish-intro', 'seen');
      veil.addEventListener('transitionend', function () { veil.remove(); }, { once: true });
      veil.classList.add('is-lifting');
      // Belt and braces if the transitionend never fires.
      setTimeout(function () { if (veil.isConnected) veil.remove(); }, 1600);
    };

    if (!hasGsap) {
      // No GSAP: still draw, using CSS.
      path.style.transition = 'stroke-dashoffset 1400ms cubic-bezier(0.65,0,0.35,1)';
      requestAnimationFrame(function () { path.style.strokeDashoffset = 0; });
      setTimeout(function () { veil.classList.add('is-filled'); }, 1400);
      setTimeout(done, 2050);
      return;
    }

    gsap.timeline({ onComplete: done })
      .to(path, { strokeDashoffset: 0, duration: 1.4, ease: 'power2.inOut' })
      .add(function () { veil.classList.add('is-filled'); })
      // the scale-up is CSS (see .intro-veil.is-filled), so both the GSAP and
      // the no-GSAP path grow the mark identically; just hold for it here.
      .to({}, { duration: 0.65 });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function boot() {
    initSmoothScroll();
    runSections(document);
    initCursorGlow();
    initIntro();
    if (hasST) ScrollTrigger.refresh();

    setTimeout(sweepReveals, 2500);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') setTimeout(sweepReveals, 300);
    });
    // A scrolled-but-unrevealed element is the failure this catches; checking
    // on the way past costs one rAF-throttled pass.
    var last = 0;
    window.addEventListener('scroll', function () {
      var now = Date.now();
      if (now - last < 120) return;
      last = now;
      sweepReveals();
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Reduced-motion can be toggled at runtime; honour it without a reload.
  var onReduceChange = function () {
    prefersReduced = mqReduce.matches;
    if (prefersReduced) {
      revealAll(document);
      stopSmoothScroll();
      if (hasST) ScrollTrigger.getAll().forEach(function (t) { t.kill(true); });
    }
  };
  if (mqReduce.addEventListener) mqReduce.addEventListener('change', onReduceChange);

  // Shopify theme editor: sections get swapped in and out live.
  document.addEventListener('shopify:section:load', function (e) {
    runSections(e.target);
    if (hasST) ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:section:unload', function () {
    if (hasST) ScrollTrigger.refresh();
  });

  window.addEventListener('pagehide', function () {
    cleanups.forEach(function (fn) { try { fn(); } catch (e) {} });
    cleanups.length = 0;
  });

  window.lavishMotion = { initSection: initSection, run: runSections };
})();
